import { useEffect, useState } from "react";
import "./BeforeAfterSection.css";

const projects = [
    {
        id: "chantier-1",
        label: "Chantier n°1",
        title: "Rénovation complète d’un appartement",

        description:
            "Redistribution des espaces, rénovation des WC, pose des sols et réalisation d’une cuisine ouverte.",

        comparisons: [
            {
                name: "Pièce de vie",
                before: "/images/avant-apres/Ch1.4.jpeg",
                after: "/images/avant-apres/Ch1.12.jpeg",
            },
            {
                name: "WC",
                before: "/images/avant-apres/Ch1.1.jpeg",
                after: "/images/avant-apres/Ch1.10.jpeg",
            },
            {
                name: "Dressing",
                before: "/images/avant-apres/Ch1.2.jpeg",
                after: "/images/avant-apres/Ch1.9.jpeg",
            },
        ],

        gallery: [
            {
                src: "/images/avant-apres/Ch1.6.jpeg",
                alt: "Salle d’eau rénovée du chantier 1",
            },
            {
                src: "/images/avant-apres/Ch1.8.jpeg",
                alt: "Chambre rénovée du chantier 1",
            },
            {
                src: "/images/avant-apres/Ch1.9.jpeg",
                alt: "Dressing aménagé du chantier 1",
            },
            {
                src: "/images/avant-apres/Ch1.10.jpeg",
                alt: "WC suspendu du chantier 1",
            },
            {
                src: "/images/avant-apres/Ch1.11.jpeg",
                alt: "Cuisine ouverte du chantier 1",
            },
            {
                src: "/images/avant-apres/Ch1.12.jpeg",
                alt: "Pièce de vie rénovée du chantier 1",
            },
        ],

        video: "/videos/chantier-1-apres.mp4",
    },

    {
        id: "chantier-2",
        label: "Chantier n°2",
        title: "Transformation intérieure complète",

        description:
            "Reprise de l’électricité, rénovation de la salle d’eau et des WC, création d’une chambre et aménagement d’une pièce de vie lumineuse.",

        comparisons: [
            {
                name: "Pièce de vie",
                before: "/images/avant-apres/Ch2.1.jpeg",
                after: "/images/avant-apres/Ch2.9.jpeg",
            },
            {
                name: "Salle d’eau",
                before: "/images/avant-apres/Ch2.4.jpeg",
                after: "/images/avant-apres/Ch2.5.jpeg",
            },
            {
                name: "WC",
                before: "/images/avant-apres/Ch2.3.jpeg",
                after: "/images/avant-apres/Ch2.6.jpeg",
            },
            {
                name: "Chambre",
                before: "/images/avant-apres/Ch2.2.jpeg",
                after: "/images/avant-apres/Ch2.7.jpeg",
            },
        ],

        gallery: [
            {
                src: "/images/avant-apres/Ch2.5.jpeg",
                alt: "Salle d’eau rénovée du chantier 2",
            },
            {
                src: "/images/avant-apres/Ch2.6.jpeg",
                alt: "WC suspendu du chantier 2",
            },
            {
                src: "/images/avant-apres/Ch2.7.jpeg",
                alt: "Chambre rénovée du chantier 2",
            },
            {
                src: "/images/avant-apres/Ch2.8.jpeg",
                alt: "Second angle de la chambre du chantier 2",
            },
            {
                src: "/images/avant-apres/Ch2.9.jpeg",
                alt: "Cuisine et pièce de vie du chantier 2",
            },
        ],

        video: "/videos/chantier-2-apres.mp4",
    },
];

function BeforeAfterViewer({ comparison }) {
    const [position, setPosition] = useState(50);

    return (
        <div className="ba-viewer">
            <img
                className="ba-image ba-before-image"
                src={comparison.before}
                alt={`${comparison.name} avant rénovation`}
            />

            <div
                className="ba-after-layer"
                style={{
                    clipPath: `inset(0 0 0 ${position}%)`,
                }}
            >
                <img
                    className="ba-image"
                    src={comparison.after}
                    alt={`${comparison.name} après rénovation`}
                />
            </div>

            <span className="ba-state-label ba-before-label">
                Avant
            </span>

            <span className="ba-state-label ba-after-label">
                Après
            </span>

            <div
                className="ba-divider"
                style={{
                    left: `${position}%`,
                }}
                aria-hidden="true"
            >
                <span className="ba-handle">
                    ‹ ›
                </span>
            </div>

            <input
                className="ba-range"
                type="range"
                min="0"
                max="100"
                value={position}
                onChange={(event) => {
                    setPosition(Number(event.target.value));
                }}
                aria-label={`Comparer l’avant et l’après : ${comparison.name}`}
            />
        </div>
    );
}

export default function BeforeAfterSection() {
    const [projectIndex, setProjectIndex] = useState(0);
    const [comparisonIndex, setComparisonIndex] =
        useState(0);

    const [selectedVideo, setSelectedVideo] =
        useState(null);

    const project = projects[projectIndex];

    const changeProject = (index) => {
        setProjectIndex(index);
        setComparisonIndex(0);
    };

    const openVideo = (video) => {
        setSelectedVideo(video);
    };

    const closeVideo = () => {
        setSelectedVideo(null);
    };

    useEffect(() => {
        if (!selectedVideo) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                closeVideo();
            }
        };

        const previousOverflow =
            document.body.style.overflow;

        document.body.style.overflow = "hidden";

        window.addEventListener(
            "keydown",
            handleKeyDown
        );

        return () => {
            document.body.style.overflow =
                previousOverflow;

            window.removeEventListener(
                "keydown",
                handleKeyDown
            );
        };
    }, [selectedVideo]);

    return (
        <section
            className="before-after-section"
            id="avant-apres"
        >
            <div className="before-after-wrapper">
                <header className="before-after-heading">
                    <p className="before-after-badge">
                        Avant / Après
                    </p>

                    <h2>
                        Des transformations qui parlent
                        <span> d’elles-mêmes</span>
                    </h2>

                    <p>
                        Faites glisser le curseur pour
                        découvrir le résultat de nos
                        rénovations.
                    </p>
                </header>

                <div
                    className="project-tabs"
                    role="tablist"
                    aria-label="Choisir un chantier"
                >
                    {projects.map((item, index) => (
                        <button
                            key={item.id}
                            type="button"
                            role="tab"
                            aria-selected={
                                projectIndex === index
                            }
                            className={`project-tab ${projectIndex === index
                                    ? "active"
                                    : ""
                                }`}
                            onClick={() =>
                                changeProject(index)
                            }
                        >
                            <span>
                                0{index + 1}
                            </span>

                            {item.label}
                        </button>
                    ))}
                </div>

                <article
                    className="project-panel"
                    role="tabpanel"
                >
                    <div className="project-copy">
                        <p className="project-kicker">
                            {project.label}
                        </p>

                        <h3>
                            {project.title}
                        </h3>

                        <p>
                            {project.description}
                        </p>
                    </div>

                    <div
                        className="room-tabs"
                        aria-label="Choisir la transformation"
                    >
                        {project.comparisons.map(
                            (comparison, index) => (
                                <button
                                    type="button"
                                    key={comparison.name}
                                    className={
                                        comparisonIndex ===
                                            index
                                            ? "active"
                                            : ""
                                    }
                                    onClick={() =>
                                        setComparisonIndex(
                                            index
                                        )
                                    }
                                >
                                    {comparison.name}
                                </button>
                            )
                        )}
                    </div>

                    <BeforeAfterViewer
                        key={`${project.id}-${comparisonIndex}`}
                        comparison={
                            project.comparisons[
                            comparisonIndex
                            ]
                        }
                    />

                    <div className="after-gallery-heading">
                        <div>
                            <p className="project-kicker">
                                Le résultat final
                            </p>

                            <h3>
                                Le chantier après rénovation
                            </h3>
                        </div>

                        <span>
                            {project.gallery.length} photos
                        </span>
                    </div>

                    <div className="after-gallery">
                        {project.gallery.map(
                            (image, index) => (
                                <figure
                                    className={
                                        index === 0
                                            ? "after-gallery-featured"
                                            : ""
                                    }
                                    key={image.src}
                                >
                                    <img
                                        src={image.src}
                                        alt={image.alt}
                                        loading="lazy"
                                    />
                                </figure>
                            )
                        )}

                        <button
                            type="button"
                            className="after-video-card"
                            onClick={() =>
                                openVideo(project.video)
                            }
                            aria-label={`Agrandir la vidéo du ${project.label}`}
                        >
                            <video
                                autoPlay
                                muted
                                loop
                                playsInline
                                preload="metadata"
                            >
                                <source
                                    src={project.video}
                                    type="video/mp4"
                                />

                                Votre navigateur ne prend
                                pas en charge cette vidéo.
                            </video>

                            <span>
                                Visite du chantier
                            </span>

                            <div
                                className="video-expand-icon"
                                aria-hidden="true"
                            >
                                ⛶
                            </div>
                        </button>
                    </div>
                </article>
            </div>

            {selectedVideo && (
                <div
                    className="video-lightbox"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Vidéo du chantier agrandie"
                    onClick={closeVideo}
                >
                    <button
                        type="button"
                        className="video-lightbox-close"
                        onClick={closeVideo}
                        aria-label="Fermer la vidéo"
                    >
                        ×
                    </button>

                    <div
                        className="video-lightbox-content"
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <video
                            autoPlay
                            muted
                            loop
                            playsInline
                            controls
                        >
                            <source
                                src={selectedVideo}
                                type="video/mp4"
                            />

                            Votre navigateur ne prend pas
                            en charge cette vidéo.
                        </video>
                    </div>
                </div>
            )}
        </section>
    );
}